import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  CalendarCheck,
  CheckCheck,
  CalendarClock,
  ExternalLink,
  FileSignature,
  FileText,
  MoreHorizontal,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useOfflineMutation } from "@/hooks/useOfflineMutation";
import { useAtuacao } from "@/lib/delegacao/atuacao";
import { queryKeys } from "@/lib/pwa/queryConfig";
import { useAcoesNoProcesso } from "@/hooks/useAcoesNoProcesso";
import { buildPrazoEventDefaults } from "@/lib/comunicacoes/prazoEvento";
import { trechoDoPrazo } from "@/lib/comunicacoes/trechoDaOrdem";

import {
  eDeColega,
  podePreparar,
  fetchCalendarEvents,
  type CalendarEvent,
  fetchComunicacao,
  markComunicacaoLida,
  type ExtractedDate,
} from "../../api/client";
import {
  EventFormDialog,
  type EventFormDefaultValues,
} from "../EventFormDialog";
import {
  formatDate,
  formatDateWithWeekday,
  formatExtractedDateRange,
  formatNumeroProcesso,
  toLocalISODate,
  toTitleCaseClasse,
} from "../../lib/formatters";
import { formatPartesResumo } from "../../lib/partes/resumo";
import { classifyError } from "../../lib/errors";
import { cn } from "../../lib/utils";
import { Alert, AlertDescription } from "../ui/alert";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { CopyButton } from "../ui/copy-button";
import {
  DrawerDetalhe,
  OQueHaParaFazer,
  SecaoDrawer,
} from "../ui/DrawerDetalhe";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { primeiroPrazoParaAgir } from "../ui/PrazoBadge";
import { rotuloDoVencimento } from "@/lib/prazos/vencimento";
import { ehPresumido, ressalvaDaLeitura, semPrazoOuIndefinido } from "@/lib/prazos/presumido";
import { ContaDoPrazo } from "./ContaDoPrazo";
import { PrazoDaLei } from "./PrazoDaLei";
import { OutrosPrazosDoTexto } from "./OutrosPrazosDoTexto";
import { PrintButton } from "../ui/print-button";
import { Spinner } from "../ui/spinner";
import { ComunicacaoPrintContent } from "./ComunicacaoPrintContent";
import { HighlightedText } from "./HighlightedText";
import { useAbrirAutos, useVaiParaNovaAba } from '@/hooks/useAbrirAutos';
import { buscaDoProtocolo } from '@/pages/processos/autos/protocolo/entradaDoProtocolo';

const tipoLabels: Record<string, string> = {
  INTIMACAO: "Intimação",
  CITACAO: "Citação",
  NOTIFICACAO: "Notificação",
  LISTA_DISTRIBUICAO: "Distribuição",
  OUTRO: "Outro",
};

const tipoDataLabels: Record<string, string> = {
  audiencia: "Audiência",
  sessao: "Sessão de julgamento",
  prazo: "Prazo",
  generico: "Data",
};

interface ComunicacaoSheetProps {
  comunicacaoId: string | null;
  onClose: () => void;
}

/**
 * O detalhe da comunicação em drawer (porte do branch do Rubens, `1c5764f3`).
 *
 * As seções estão na ordem em que a pergunta aparece: de onde veio, o que há
 * para fazer, o que o juízo escreveu, que datas o texto cita. O que há para
 * fazer vem ANTES do texto — é a resposta; o texto é a prova dela.
 */
export function ComunicacaoSheet({
  comunicacaoId,
  onClose,
}: ComunicacaoSheetProps) {
  const navigate = useNavigate();
  const abrirAutos = useAbrirAutos();
  const vaiParaNovaAba = useVaiParaNovaAba();
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarDefaults, setCalendarDefaults] = useState<
    EventFormDefaultValues | undefined
  >();
  const [showAllDatas, setShowAllDatas] = useState(false);
  // O prazo do processo do colega, aberto para ajustar no formulário (Bloco 3).
  const [eventoEmEdicao, setEventoEmEdicao] = useState<CalendarEvent | null>(null);

  const {
    data: carregada,
    isLoading,
    error,
  } = useQuery({
    queryKey: [...queryKeys.comunicacoes.detail(comunicacaoId!)],
    queryFn: () => fetchComunicacao(comunicacaoId!),
    enabled: !!comunicacaoId,
  });
  // Ao fechar, o id vira null e a consulta esvazia ainda durante a animação de
  // saída: a última comunicação fica na tela até o drawer sumir, em vez de um
  // "Carregando" piscando.
  const [ultimaCarregada, setUltimaCarregada] = useState(carregada);
  if (carregada && carregada !== ultimaCarregada) setUltimaCarregada(carregada);
  const comunicacao =
    carregada ?? (comunicacaoId ? undefined : ultimaCarregada);

  const { data: linkedEvents = [] } = useQuery({
    queryKey: [
      ...queryKeys.calendario.eventos,
      "by-comunicacao",
      comunicacaoId,
    ],
    queryFn: () => fetchCalendarEvents(undefined, undefined, comunicacaoId!),
    enabled: !!comunicacaoId,
  });
  const linkedEventDates = new Set(
    linkedEvents.map((e) => e.data_inicio.slice(0, 10)),
  );
  const prazoEvent = linkedEvents.find((e) => e.tipo === "prazo");

  const markLidaMutation = useOfflineMutation<
    { id: string; lida: boolean },
    string
  >({
    // Abrir e "Marcar como lida" querem "lida", nunca alternar: reenviado pela fila
    // depois que outra pessoa leu, o toggle a voltaria para não lida.
    mutationFn: (id: string) => markComunicacaoLida(id, true),
    outbox: {
      type: "comunicacao_lida",
      getPayload: (id) => ({
        method: "PATCH",
        url: `/comunicacoes/${id}/lida`,
        body: { lida: true },
      }),
      getDescription: () => "Marcar comunicação como lida",
    },
    invalidateKeys: [
      [...queryKeys.comunicacoes.all],
      [...queryKeys.comunicacoes.stats],
      [...queryKeys.dashboard.stats],
    ],
  });

  // Ref para evitar enfileirar duplicatas no outbox ao reabrir offline.
  // Delegação só de Leitura não escreve nada na conta do titular: abrir a
  // intimação não pode virar "lida" para ele (o backend responderia 403). Só
  // o que ESCREVE passa por este portão; ver o processo e abrir no tribunal
  // seguem livres.
  const atuacao = useAtuacao();
  // A de um colega (visão do escritório): quem colabora no processo dele marca lida e
  // agenda o prazo — no registro e na agenda do dono (Bloco 3). Em Leitura, só se lê.
  const doColega = !!comunicacao && eDeColega(comunicacao);
  // Quem entra pela assinatura do escritório também marca e agenda no do colega (3b).
  const podeMarcar =
    (!atuacao || atuacao.nivel === "COLABORACAO") && !(comunicacao && !podePreparar(comunicacao));
  // No do colega, o advogado peticiona com o próprio certificado; quem não protocola
  // prepara a petição para o dono protocolar (A3).
  const acoes = useAcoesNoProcesso(comunicacao);
  const podePeticionar = acoes.podePeticionar;
  // Abrir marca como lida só a própria: a do colega fica não lida até alguém dizer que leu
  // (senão quem só passou os olhos tirava a intimação da fila de quem cuida dela).
  const marcaAoAbrir = podeMarcar && !doColega;

  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      marcaAoAbrir &&
      comunicacao &&
      !comunicacao.lida &&
      markedRef.current !== comunicacao.id
    ) {
      markedRef.current = comunicacao.id;
      markLidaMutation.mutate(comunicacao.id);
    }
  }, [marcaAoAbrir, comunicacao, markLidaMutation]);

  const [prevComunicacaoId, setPrevComunicacaoId] = useState(comunicacaoId);
  if (comunicacaoId !== prevComunicacaoId) {
    setPrevComunicacaoId(comunicacaoId);
    setShowAllDatas(false);
  }

  const buildDefaults = (d: ExtractedDate): EventFormDefaultValues => {
    const tipoLabel = tipoDataLabels[d.tipo] ?? "Evento";
    const horaInicio = d.hora || "00:00";
    const horaFim = d.hora_fim || horaInicio;
    const dataFimIso = d.data_fim
      ? `${d.data_fim}T${horaFim}`
      : d.hora_fim && d.hora_fim !== d.hora
        ? `${d.data}T${horaFim}`
        : undefined;
    return {
      titulo: `${tipoLabel} — ${toTitleCaseClasse(comunicacao?.nome_classe ?? "") || comunicacao?.numero_processo || "Processo"}`,
      tipo: d.tipo === "generico" ? "outro" : d.tipo,
      data_inicio: `${d.data}T${horaInicio}`,
      data_fim: dataFimIso,
      processo_id: comunicacao?.processo_id ?? undefined,
    };
  };

  const handleDateClick = (d: ExtractedDate) => {
    setCalendarDefaults(buildDefaults(d));
    setCalendarDialogOpen(true);
  };

  const handleAddToCalendar = () => {
    const primeira = comunicacao?.datas_extraidas?.find(
      (d) =>
        d.tipo === "audiencia" || d.tipo === "sessao" || d.tipo === "prazo",
    );
    if (primeira) {
      setCalendarDefaults(buildDefaults(primeira));
      setCalendarDialogOpen(true);
    }
  };

  // O sistema NÃO calcula nem sugere a data final do prazo: abre o formulário
  // com tipo e processo vinculados, e a data fica com o advogado.
  const handleAddPrazoToCalendar = () => {
    if (!comunicacao) return;
    setCalendarDefaults(buildPrazoEventDefaults(comunicacao));
    setCalendarDialogOpen(true);
  };

  const fechar = () => onClose();
  const aberto = !!comunicacaoId;

  // O formulário de evento fica montado em qualquer estado: um refetch que
  // falha com ele aberto não pode levar o que o usuário digitou.
  const formularioDeEvento = (
    <>
      <EventFormDialog
        open={calendarDialogOpen}
        onClose={() => setCalendarDialogOpen(false)}
        defaultValues={calendarDefaults}
      />
      <EventFormDialog
        open={!!eventoEmEdicao}
        onClose={() => setEventoEmEdicao(null)}
        event={eventoEmEdicao}
      />
    </>
  );

  // Erro de um refetch com a comunicação já na tela não a tira dali.
  if (!comunicacao) {
    return (
      <>
        <DrawerDetalhe
          aberto={aberto}
          onFechar={fechar}
          rotulo="A comunicação"
          titulo={error && !isLoading ? "Não foi possível abrir" : "Carregando"}
          numero=""
          acoes={
            <Button
              variant="outline"
              className="min-h-[44px] flex-1"
              onClick={fechar}
            >
              Fechar
            </Button>
          }
        >
          <div className="px-6 py-8">
            {error && !isLoading ? (
              <Alert variant="destructive">
                <AlertDescription>
                  {classifyError(error).message}
                </AlertDescription>
              </Alert>
            ) : (
              <div className="flex justify-center">
                <Spinner size={32} label="Carregando a comunicação" />
              </div>
            )}
          </div>
        </DrawerDetalhe>
        {formularioDeEvento}
      </>
    );
  }

  const isDjen = comunicacao.fontes?.some((f) => f.fonte === "DJEN") ?? false;
  // O vencimento veio da lei, não do texto: nenhum trecho do texto é "o prazo"
  // (um "400 dias" citado não pode levar a data da apelação).
  const daLei = ehPresumido(comunicacao.prazo_calculo);
  // Com conta do sistema, a citação é a do prazo que foi CONTADO (o backend
  // conta só dias; "48 horas" citado antes não pode emprestar a data dos 15 dias).
  // Pela IA, o prazo é o que ela atribuiu à parte — nunca o "primeiro do texto", que
  // pode ser o do perito (Bloco 4).
  // Também quando a IA concluiu que não há prazo para a parte (o do texto é do perito):
  // aí não há conta, e a decisão vem no prazo_presumido.
  const pelaIa = !!comunicacao.prazo_calculo?.ia || !!comunicacao.prazo_presumido?.ia;
  const prazoDoTexto = daLei
    ? null
    : (comunicacao.prazo_calculo?.posicao != null &&
        comunicacao.prazos_extraidos?.find(
          (p) => p.posicao === comunicacao.prazo_calculo?.posicao,
        )) ||
      (pelaIa ? null : primeiroPrazoParaAgir(comunicacao.prazos_extraidos));
  // Sem prazo no texto nem da lei: o que a leitura do ato concluiu (Bloco 3).
  const semPrazo = semPrazoOuIndefinido(comunicacao);
  // A frase que o drawer mostra como tarefa: a citação da própria ordem.
  // Nunca uma frase nossa sobre o que o juízo "quis dizer".
  const oQueFazer = prazoDoTexto?.contexto
    ? `“${trechoDoPrazo(prazoDoTexto, comunicacao.texto)}”`
    : !daLei && comunicacao.prazo_calculo?.trecho
      ? `“${comunicacao.prazo_calculo.trecho}”`
      : null;
  // Os demais prazos do texto — inclusive os em meses ou anos, que não viram
  // tarefa mas continuam citados. Cada um leva ao trecho destacado no texto.
  const outrosPrazos = (comunicacao.prazos_extraidos ?? []).filter(
    (p) => p !== prazoDoTexto,
  );
  const listaDeOutrosPrazos = outrosPrazos.length > 0 && (
    <OutrosPrazosDoTexto prazos={outrosPrazos} irPara={irParaPrazoNoTexto} />
  );
  // A conta do vencimento feita pelo sistema (ausente: data de outra origem).
  const calculo = comunicacao.prazo_calculo ?? null;
  const vencimento = calculo
    ? rotuloDoVencimento(calculo.vencimento, calculo.estimado)
    : null;
  const datasRelevantes =
    comunicacao.datas_extraidas?.filter(
      (d) =>
        d.tipo === "audiencia" || d.tipo === "sessao" || d.tipo === "prazo",
    ) ?? [];
  const MAX_DATAS = 4;
  const datasVisiveis = showAllDatas
    ? datasRelevantes
    : datasRelevantes.slice(0, MAX_DATAS);

  const temDataPorAgendar = datasRelevantes.some(
    (d) => !linkedEventDates.has(d.data),
  );
  const temMaisAcoes =
    (podeMarcar &&
      ((!!comunicacao.processo_id && podePeticionar) ||
        temDataPorAgendar ||
        !!prazoEvent ||
        (doColega && !comunicacao.lida))) ||
    !!comunicacao.link;

  return (
    <>
      <DrawerDetalhe
        aberto={aberto}
        onFechar={fechar}
        rotulo="A comunicação"
        // A mesma cor da espinha da linha: há prazo a cumprir, ou não há.
        faixa={
          prazoDoTexto || comunicacao.prazo_resposta
            ? "bg-[var(--color-gold)]"
            : "bg-border"
        }
        titulo={toTitleCaseClasse(
          comunicacao.assunto || comunicacao.nome_classe || "Comunicação",
        )}
        numero={formatNumeroProcesso(comunicacao.numero_processo)}
        onAbrirAutos={
          comunicacao.processo_id
            ? () => {
                fechar();
                navigate(
                  `/app/processos/${encodeURIComponent(comunicacao.numero_processo)}/autos`,
                );
              }
            : undefined
        }
        partes={formatPartesResumo(comunicacao.destinatarios_json)}
        selos={
          <>
            <Badge variant="outline">
              {tipoLabels[comunicacao.tipo ?? ""] ??
                comunicacao.tipo ??
                "Outro"}
            </Badge>
            {comunicacao.sigla_tribunal && (
              <Badge variant="outline">{comunicacao.sigla_tribunal}</Badge>
            )}
            {isDjen && <Badge variant="outline">DJEN</Badge>}
            {!isDjen && comunicacao.status_ciencia && (
              <Badge variant="outline">
                {comunicacao.status_ciencia === "REALIZADA"
                  ? "Ciência realizada"
                  : "Ciência pendente"}
              </Badge>
            )}
          </>
        }
        acoes={
          <>
            {comunicacao.processo_id && (
              <Button
                className="min-h-[44px] flex-1 justify-center gap-2 whitespace-normal break-words text-base"
                onClick={(e) => {
                  // Na mesma aba a intimação sai de cena; numa aba nova ela fica aberta aqui.
                  if (!vaiParaNovaAba(e)) fechar();
                  abrirAutos(comunicacao.numero_processo, { evento: e });
                }}
                onAuxClick={(e) => e.button === 1 && abrirAutos(comunicacao.numero_processo, { evento: e })}
              >
                <FileText className="h-4 w-4 flex-none" />
                Ver o processo
              </Button>
            )}
            {prazoEvent ? (
              <Button
                variant="outline"
                className="min-h-[44px] flex-1 justify-center gap-2 whitespace-normal break-words text-base"
                disabled={doColega && !podeMarcar}
                onClick={() => {
                  // O prazo do colega está na agenda DELE (A9): abre aqui, para ajustar.
                  if (doColega) {
                    setEventoEmEdicao(prazoEvent);
                    return;
                  }
                  fechar();
                  navigate(
                    `/app/calendario?data=${toLocalISODate(prazoEvent.data_inicio)}`,
                  );
                }}
              >
                <CalendarCheck className="h-4 w-4 flex-none" />
                Prazo em{" "}
                {new Date(prazoEvent.data_inicio).toLocaleDateString("pt-BR")}
              </Button>
            ) : podeMarcar ? (
              <Button
                variant="outline"
                className="min-h-[44px] flex-1 justify-center gap-2 whitespace-normal break-words text-base"
                onClick={handleAddPrazoToCalendar}
              >
                <CalendarClock className="h-4 w-4 flex-none" />
                Cadastrar prazo
              </Button>
            ) : null}
            {/* As demais ações ficam no “mais”: continuam todas alcançáveis,
                sem transformar o rodapé numa escada de cinco botões. */}
            {temMaisAcoes && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-auto min-h-[44px] w-11 flex-none"
                    aria-label="Mais ações"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {podeMarcar && doColega && !comunicacao.lida && (
                    <DropdownMenuItem onClick={() => markLidaMutation.mutate(comunicacao.id)}>
                      <CheckCheck className="mr-3 h-4 w-4" /> Marcar como lida
                    </DropdownMenuItem>
                  )}
                  {podeMarcar && podePeticionar && comunicacao.processo_id && (
                    <DropdownMenuItem
                      onClick={() => {
                        fechar();
                        abrirAutos(comunicacao.numero_processo, { search: buscaDoProtocolo() });
                      }}
                    >
                      <FileSignature className="mr-3 h-4 w-4" /> {acoes.rotuloPeticionar}
                    </DropdownMenuItem>
                  )}
                  {podeMarcar && temDataPorAgendar && (
                    <DropdownMenuItem onClick={handleAddToCalendar}>
                      <Calendar className="mr-3 h-4 w-4" /> Adicionar à agenda
                    </DropdownMenuItem>
                  )}
                  {podeMarcar && prazoEvent && (
                    <DropdownMenuItem onClick={handleAddPrazoToCalendar}>
                      <CalendarClock className="mr-3 h-4 w-4" /> Cadastrar outro
                      prazo
                    </DropdownMenuItem>
                  )}
                  {comunicacao.link && (
                    <DropdownMenuItem
                      onClick={() => window.open(comunicacao.link!, "_blank")}
                    >
                      <ExternalLink className="mr-3 h-4 w-4" /> Abrir no
                      tribunal
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </>
        }
      >
        <SecaoDrawer titulo="De onde veio">
          <dl className="flex flex-wrap gap-x-7 gap-y-1.5 text-base">
            {comunicacao.nome_orgao && (
              <div className="flex w-full min-w-0 gap-2">
                <dt className="flex-none text-muted-foreground">Órgão</dt>
                <dd className="min-w-0 break-words">
                  {comunicacao.nome_orgao}
                </dd>
              </div>
            )}
            {comunicacao.data_disponibilizacao && (
              <div className="flex min-w-0 gap-2">
                <dt className="flex-none text-muted-foreground">
                  Disponibilizada
                </dt>
                <dd className="min-w-0">
                  {formatDateWithWeekday(comunicacao.data_disponibilizacao)}
                </dd>
              </div>
            )}
            {!isDjen && comunicacao.data_ciencia && (
              <div className="flex min-w-0 gap-2">
                <dt className="flex-none text-muted-foreground">Ciência</dt>
                <dd className="min-w-0">
                  {formatDate(comunicacao.data_ciencia)}
                </dd>
              </div>
            )}
            {/* Com trecho do prazo, a data vai na caixa do que há para fazer;
                sem ele, fica aqui — não se perde. */}
            {comunicacao.prazo_resposta && !oQueFazer && !daLei && (
              <div className="flex min-w-0 gap-2">
                <dt className="flex-none text-muted-foreground">Prazo</dt>
                <dd className="min-w-0 font-semibold text-destructive">
                  {vencimento
                    ? vencimento.longo
                    : formatDate(comunicacao.prazo_resposta)}
                </dd>
              </div>
            )}
            {comunicacao.recebida_por && (
              <div className="flex w-full min-w-0 gap-2">
                <dt className="flex-none text-muted-foreground">Recebida por</dt>
                <dd className="min-w-0 break-words">
                  {comunicacao.recebida_por.nome || "Um colega"}
                </dd>
              </div>
            )}
            {comunicacao.lida && comunicacao.lida_por_nome && (
              <div className="flex w-full min-w-0 gap-2">
                <dt className="flex-none text-muted-foreground">Lida por</dt>
                <dd className="min-w-0 break-words">
                  {comunicacao.lida_por_nome}
                </dd>
              </div>
            )}
          </dl>
        </SecaoDrawer>

        {oQueFazer && (
          <SecaoDrawer titulo="O que há para fazer">
            <OQueHaParaFazer
              frase={oQueFazer}
              origem={
                vencimento
                  ? `${vencimento.longo}.`
                  : comunicacao.prazo_resposta
                    ? `Vence em ${formatDate(comunicacao.prazo_resposta)}.`
                    : "Trecho do próprio despacho, abaixo."
              }
              ressalva={
                calculo
                  ? [
                      ressalvaDaLeitura(calculo) ??
                        "Conta do sistema, a partir da disponibilização no DJEN — confira no tribunal.",
                      calculo.estimado ? calculo.motivo_estimado : null,
                      calculo.aviso_regime,
                    ]
                      .filter(Boolean)
                      .join(" ")
                  : "A forma de contagem, dias úteis ou corridos, e a data final devem ser conferidas no tribunal — o sistema não as calcula."
              }
            />
            {calculo && (
              <ContaDoPrazo
                calculo={calculo}
                // A data fixada pelo juiz não muda com feriado: não há conta a refazer.
                podeLancar={podeMarcar && !doColega && !calculo.data_fixada}
              />
            )}
            {listaDeOutrosPrazos && (
              <div className="mt-3">
                <p className="mb-1.5 text-xs text-muted-foreground">
                  O texto também menciona:
                </p>
                {listaDeOutrosPrazos}
              </div>
            )}
          </SecaoDrawer>
        )}

        {/* Como no prazo do texto: o trecho que gerou o prazo (aqui, o ato) e o prazo.
            Também sem o vencimento do principal (data de outra origem, conta que não
            deu): os prazos da lei como informação. */}
        {!oQueFazer && comunicacao.prazo_estado === "presumido" && (
          <SecaoDrawer titulo="O que há para fazer">
            <PrazoDaLei
              comunicacao={comunicacao}
              podeLancar={podeMarcar && !doColega}
            />
          </SecaoDrawer>
        )}

        {/* Com data na intimação (de outra origem), a tela não diz que não há prazo. */}
        {!oQueFazer && !daLei && !comunicacao.prazo_resposta && semPrazo && (
          <SecaoDrawer titulo="O que há para fazer">
            <p className="text-base">{semPrazo.titulo}</p>
            {semPrazo.detalhe && (
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                {semPrazo.detalhe}
              </p>
            )}
          </SecaoDrawer>
        )}

        {!oQueFazer && listaDeOutrosPrazos && (
          <SecaoDrawer titulo="Prazos que o texto menciona">
            {listaDeOutrosPrazos}
            <p className="mt-2 text-xs text-muted-foreground">
              Lidos automaticamente do texto — a contagem e a data final devem ser conferidas no
              tribunal.
            </p>
          </SecaoDrawer>
        )}

        {comunicacao.texto && (
          <SecaoDrawer
            titulo="O texto, como veio do Diário"
            // Ocupa o que sobra e rola por dentro: as datas abaixo ficam à
            // vista enquanto se lê. O mínimo garante leitura em tela baixa
            // (aí quem rola é o corpo inteiro).
            className="flex min-h-[16rem] flex-1 flex-col"
            ultima={datasRelevantes.length === 0}
            acaoNoTitulo={
              <>
                <CopyButton
                  text={comunicacao.texto}
                  label="Copiar o texto da comunicação"
                />
                <PrintButton
                  content={
                    <ComunicacaoPrintContent comunicacao={comunicacao} />
                  }
                  mode="print"
                  documentTitle={`Comunicação - ${comunicacao.numero_processo}`}
                />
                <PrintButton
                  content={
                    <ComunicacaoPrintContent comunicacao={comunicacao} />
                  }
                  mode="pdf"
                  documentTitle={`Comunicação - ${comunicacao.numero_processo}`}
                />
              </>
            }
          >
            {/* Fundo de papel e fio: é citação de documento, não texto nosso.
                Rolagem própria, para as datas e o rodapé ficarem fixos. */}
            <div
              tabIndex={0}
              aria-label="Texto da comunicação"
              className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-border bg-muted/25 px-4 py-3.5 text-[15px] leading-relaxed text-foreground/85"
            >
              <HighlightedText
                texto={comunicacao.texto}
                datas={(comunicacao.datas_extraidas || []).filter(
                  (d) => d.tipo === "audiencia" || d.tipo === "sessao",
                )}
                prazos={comunicacao.prazos_extraidos ?? undefined}
                trechoDoAto={
                  comunicacao.prazo_estado === "presumido"
                    ? comunicacao.prazo_presumido?.trecho
                    : undefined
                }
                scheduledDates={linkedEventDates}
                onDateClick={handleDateClick}
                onScheduledDateClick={(d) => {
                  fechar();
                  navigate(`/app/calendario?data=${d.data}`);
                }}
              />
            </div>
          </SecaoDrawer>
        )}

        {datasRelevantes.length > 0 && (
          <SecaoDrawer
            titulo="Datas que o texto menciona"
            ultima
            className="max-h-[35vh] overflow-y-auto"
          >
            <ul className="flex flex-col gap-1.5">
              {datasVisiveis.map((d, i) => {
                const jaAgendada = linkedEventDates.has(d.data);
                return (
                  <li key={i}>
                    <Button
                      type="button"
                      variant="unstyled"
                      onClick={
                        jaAgendada
                          ? () => {
                              fechar();
                              navigate(`/app/calendario?data=${d.data}`);
                            }
                          : () => handleDateClick(d)
                      }
                      className={cn(
                        "flex h-auto min-h-[44px] w-full items-center justify-between gap-3 whitespace-normal rounded-lg border px-3 py-2 text-left font-normal transition-colors",
                        jaAgendada
                          ? "border-border bg-background hover:bg-accent"
                          : "border-border bg-muted/40 hover:bg-muted/70",
                      )}
                    >
                      <span className="min-w-0 break-words text-base">
                        {formatExtractedDateRange(d)}
                      </span>
                      <span
                        className={cn(
                          "flex-none text-xs",
                          jaAgendada
                            ? "font-semibold text-[var(--color-success)]"
                            : "text-muted-foreground",
                        )}
                      >
                        {jaAgendada
                          ? "Já na agenda"
                          : `${tipoDataLabels[d.tipo] ?? "Data"} · cadastrar`}
                      </span>
                    </Button>
                  </li>
                );
              })}
            </ul>
            {datasRelevantes.length > MAX_DATAS && !showAllDatas && (
              <Button
                variant="ghost"
                className="mt-2 min-h-[44px] w-full text-xs text-muted-foreground"
                onClick={() => setShowAllDatas(true)}
              >
                Ver todas as {datasRelevantes.length} datas
              </Button>
            )}
          </SecaoDrawer>
        )}
      </DrawerDetalhe>

      {formularioDeEvento}
    </>
  );
}

/** Leva ao prazo destacado no texto e o acende por um instante. */
function irParaPrazoNoTexto(posicao: number) {
  const el = document.getElementById(`prazo-${posicao}`);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "center" });
  el.classList.add("ring-2", "ring-primary", "ring-offset-1");
  setTimeout(
    () => el.classList.remove("ring-2", "ring-primary", "ring-offset-1"),
    1500,
  );
}
